/**
 * The header's product search — design/PROTOTYPE-SPEC.md §2: "search box with
 * live suggestions dropdown". Type a few letters on any page, pick a product,
 * and its drawer opens where you are.
 *
 * It is a jump, not a filter. Inventory has its own search that narrows the
 * table; this one answers "where is the Azzalure?" from the Dashboard, the
 * Expiry page or anywhere else, without a trip to Inventory first.
 *
 * ## Every keystroke fires a request, so both guards are here
 *
 * A 200ms debounce AND a monotonic sequence number whose stale responses are
 * dropped. Debounce alone only narrows the window: type "az", then "azz", and
 * the slower first answer can land second and replace the right list with the
 * wrong one (DECISIONS.md, "Every keystroke firing a request needs
 * sequencing"). That bug shipped once on the Expiry page; this copies its fix.
 */
import { useEffect, useRef, useState } from 'react';
import { listProducts, type ProductRow } from '../lib/api.js';
import { c, sans } from '../theme.js';

const MAX_SUGGESTIONS = 6;

interface Props {
  /** Opens the product's drawer. */
  onPick: (productId: string) => void;
  /** Bumped after any write, so an open list does not show a stale quantity. */
  refreshToken: number;
}

export function HeaderSearch({ onPick, refreshToken }: Props) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<ProductRow[] | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const latest = useRef(0);
  const box = useRef<HTMLDivElement>(null);

  const query = term.trim();

  useEffect(() => {
    if (query.length < 2) {
      latest.current += 1; // invalidate anything in flight
      setResults(null);
      return;
    }
    const seq = ++latest.current;
    const timer = setTimeout(() => {
      listProducts(`?${new URLSearchParams({ q: query }).toString()}`)
        .then((res) => {
          if (seq !== latest.current) return;
          setResults(res.products.slice(0, MAX_SUGGESTIONS));
          setActive(0);
        })
        .catch(() => {
          if (seq === latest.current) setResults([]);
        });
    }, 200);
    return () => clearTimeout(timer);
  }, [query, refreshToken]);

  // Click anywhere else closes the list.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  function pick(product: ProductRow) {
    onPick(product.id);
    setTerm('');
    setResults(null);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!results || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const chosen = results[active];
      if (chosen) pick(chosen);
    }
  }

  const showList = open && query.length >= 2 && results !== null;

  return (
    <div ref={box} className="gs-search" style={{ position: 'relative', width: 250 }}>
      <input
        type="search"
        role="combobox"
        aria-label="Search products"
        aria-expanded={showList}
        aria-controls="header-search-list"
        aria-autocomplete="list"
        placeholder="Search products"
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        style={{
          width: '100%',
          height: 36,
          boxSizing: 'border-box',
          padding: '0 12px',
          borderRadius: 8,
          border: `1px solid ${c.border}`,
          background: c.card,
          fontFamily: sans,
          fontSize: 13.5,
          color: c.ink,
          outline: 'none',
        }}
      />

      {showList && (
        <ul
          id="header-search-list"
          role="listbox"
          style={{
            position: 'absolute',
            top: 50,
            left: 0,
            right: 0,
            margin: 0,
            padding: 6,
            listStyle: 'none',
            background: c.white,
            border: `1px solid ${c.borderStrong}`,
            borderRadius: 18,
            boxShadow: '0 18px 40px -14px rgba(20,26,22,0.35)',
            zIndex: 40,
          }}
        >
          {results.length === 0 ? (
            <li style={{ padding: '10px 12px', fontSize: 13, color: c.muted }}>
              No product matches “{query}”.
            </li>
          ) : (
            results.map((p, i) => (
              <li
                key={p.id}
                role="option"
                aria-selected={i === active}
                // mousedown, not click: the input's blur must not close the list
                // before the choice registers.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(p);
                }}
                onMouseEnter={() => setActive(i)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '9px 12px',
                  borderRadius: 980,
                  cursor: 'pointer',
                  background: i === active ? c.well : 'transparent',
                  fontSize: 13.5,
                }}
              >
                <span
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {p.name}
                  {p.brand && <span style={{ color: c.muted }}> · {p.brand}</span>}
                </span>
                <span style={{ color: c.muted, whiteSpace: 'nowrap' }}>{p.qtyOnHand} left</span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
